/*
 * Server detail view functions
 * This has been extensively refactored by Claude to use JSON endpoints for the server card and action buttons, so that each can be updated independently when its data changes.
 */

(function (__module__, c2, $) {
  'use strict';

  var logger = c2.logging.getLogger(__module__);
  var updateTimer, mediumTimeout, longTimeout;

  /*
   * There is a schedule for live updates, to avoid excessive load on CB servers:
   *     For the first 5 minutes, every 10 seconds.
   *     After 5 minutes, once a minute.
   *     After an hour, every 5 minutes.
   */
  var prodDurations = {
    shortInterval: luxon.Duration.fromObject({ seconds: 10 }).toMillis(),
    mediumStart:   luxon.Duration.fromObject({ minutes: 5 }).toMillis(),
    mediumInterval: luxon.Duration.fromObject({ minutes: 1 }).toMillis(),
    longStart:     luxon.Duration.fromObject({ hours: 1 }).toMillis(),
    longInterval:  luxon.Duration.fromObject({ minutes: 5 }).toMillis()
  };
  var testDurations = {
    shortInterval: luxon.Duration.fromObject({ seconds: 5 }).toMillis(),
    mediumStart:   luxon.Duration.fromObject({ seconds: 20 }).toMillis(),
    mediumInterval: luxon.Duration.fromObject({ seconds: 10 }).toMillis(),
    longStart:     luxon.Duration.fromObject({ minutes: 1 }).toMillis(),
    longInterval:  luxon.Duration.fromObject({ seconds: 20 }).toMillis()
  };
  var durations, disableAutoRefresh;

  // URLs for JSON endpoints
  var _cardStatusUrl, _actionsDataUrl;

  // Last-seen serialised JSON — null until first successful fetch
  var _lastCardStatusKey = null;
  var _lastActionsDataKey = null;

  // ─── Public entry point ───────────────────────────────────────────────────

  /*
   * Initialise live updates for the server details page.
   *
   * The sidebar (server card + actions) is pre-rendered by the server on initial
   * page load.  This function sets up two independent JSON pollers — one for the
   * card panel (power state, lock info) and one for action buttons — so each can
   * be updated only when its data actually changes.
   */
  function liveUpdates(cardStatusUrl, actionsDataUrl, disableAutoRefreshVar) {
    // Change to testDurations for faster reload intervals during development
    durations = prodDurations;
    disableAutoRefresh = disableAutoRefreshVar;
    _cardStatusUrl = cardStatusUrl;
    _actionsDataUrl = actionsDataUrl;

    $(document).on('cb.inlineEdits.loaded', stopUpdates);
    $(document).on('cb.inlineEdits.closed', startUpdates);

    if (disableAutoRefresh == false) {
      startUpdates();
    }

    _setupPageVisibilityHandler();
    _setupRefreshButtonHandler();
  }


  // ─── Polling ──────────────────────────────────────────────────────────────

  /*
   * Reload all live sections.  Called on a timer and by the Refresh button.
   * The JSON fetches are independent; each skips DOM work when data is unchanged.
   * Removed Jobs table and history table refresh
   */
  function reloadSections() {
    logger.debug('reloadSections');
    _fetchCardStatus();
    _fetchActionsData();
    _updateTimestamp();
  }


  function _fetchCardStatus() {
    fetch(_cardStatusUrl, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        _showLiveUpdatesInfo();
        _hideOfflineAlert();
        var key = JSON.stringify(data);
        if (key !== _lastCardStatusKey) {
          _lastCardStatusKey = key;
          _applyCardStatus(data);
        }
      })
      .catch(function (err) {
        logger.error('card_status fetch failed:', err);
        _showOfflineAlert();
        stopUpdates();
      });
  }


  function _fetchActionsData() {
    fetch(_actionsDataUrl, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var key = JSON.stringify(data);
        if (key !== _lastActionsDataKey) {
          _lastActionsDataKey = key;
          _applyActionsData(data);
        }
      })
      .catch(function (err) {
        logger.error('actions_data fetch failed:', err);
      });
  }

  // ─── DOM update: card panel ───────────────────────────────────────────────
  function _updateTimestamp() {
    $('[data-include-timestamp]').text('Last updated ' + luxon.DateTime.now().toFormat('MMMM d yyyy, h:mm:ss a')).removeClass('d-none');
  }
  function _applyCardStatus(data) {
    // Power state span — update CSS class and visible text
    var powerStateEl = document.getElementById('server-power-state');
    if (powerStateEl) {
      powerStateEl.className = 'power-state ' + data.power_status;
      powerStateEl.textContent = data.power_status_display;
    }

    // "Powered" prefix text — hidden when server is suspended
    var poweredTextEl = document.getElementById('server-powered-text');
    if (poweredTextEl) {
      if (data.is_suspended) {
        poweredTextEl.textContent = '';
      } else {
        // Retrieve the translated label stored on the parent element at render time
        var displayEl = document.getElementById('server-power-display');
        var label = displayEl ? (displayEl.getAttribute('data-powered-label') || 'Powered') : 'Powered';
        poweredTextEl.textContent = label;
      }
    }

    // Locked-server message
    var lockedEl = document.getElementById('server-locked-display');
    if (lockedEl) {
      if (data.locked_msg) {
        lockedEl.classList.remove('d-none');
        var msgSpan = lockedEl.querySelector('#server-locked-display-msg');
        if (msgSpan) {
          msgSpan.textContent = data.locked_msg;
        }
        // Update tooltip title if the infotip element is present
        var tooltipEl = lockedEl.querySelector('[data-bs-toggle="tooltip"]');
        if (tooltipEl && data.info_tip) {
          tooltipEl.setAttribute('data-bs-title', data.info_tip);
          // Dispose and recreate the Bootstrap tooltip so the new title takes effect
          var existingTooltip = bootstrap.Tooltip.getInstance(tooltipEl);
          if (existingTooltip) {
            existingTooltip.dispose();
          }
          new bootstrap.Tooltip(tooltipEl);
        }
      } else {
        lockedEl.classList.add('d-none');
      }
    }
  }


  // ─── DOM update: action buttons ───────────────────────────────────────────

  function _applyActionsData(data) {
    var container = document.getElementById('server-actions-container');
    if (!container) return;

    // Preserve the inline <script> at the end of the container (the Refresh Info
    // spinner wiring added by the template) so we don't lose it on rebuild.
    var scriptEl = container.querySelector('script');

    // Determine layout from the existing #server-actions element's classes
    var existingActions = document.getElementById('server-actions');
    var isSidebar = existingActions
      ? existingActions.classList.contains('btn-group-vertical')
      : !!document.getElementById('server-details-sidebar');

    // Clear everything except the preserved script
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    if (isSidebar) {
      _buildSidebarActions(container, data.actions, data.can_run_action);
    } else {
      _buildToolbarActions(container, data.actions, data.can_run_action);
    }

    // Restore the inline script (re-wires the Refresh Info spinner)
    if (scriptEl) {
      var newScript = document.createElement('script');
      newScript.textContent = scriptEl.textContent;
      container.appendChild(newScript);
    }

    // Re-initialise open-dialog and tooltip bindings on the new elements
    if (c2.dialogs && c2.dialogs.init) c2.dialogs.init();
    if (c2.tooltips && c2.tooltips.init) c2.tooltips.init();
  }


  // Sidebar layout: vertical button groups separated by optional section headings
  function _buildSidebarActions(container, actions, canRunAction) {
    var group = document.createElement('div');
    group.id = 'server-actions';
    group.className = 'btn-group-vertical';
    container.appendChild(group);

    for (var i = 0; i < actions.length; i++) {
      var action = actions[i];
      if (action.new_section) {
        if (action.label) {
          var h4 = document.createElement('h4');
          h4.textContent = action.label;
          container.appendChild(h4);
        }
        group = document.createElement('div');
        group.className = 'btn-group-vertical';
        container.appendChild(group);
      } else {
        group.appendChild(_createActionLink(action, canRunAction));
      }
    }
  }


  // Toolbar layout: first 5 items inline, remainder in a "More" dropdown
  function _buildToolbarActions(container, actions, canRunAction) {
    var toolbar = document.createElement('div');
    toolbar.id = 'server-actions';
    toolbar.className = 'gap-2 d-flex justify-content-start';
    container.appendChild(toolbar);

    var currentGroup = document.createElement('div');
    currentGroup.className = 'btn-toolbar gap-2';
    toolbar.appendChild(currentGroup);

    var dropdownUl = null;
    var dropdownGroup = null;

    for (var i = 0; i < actions.length; i++) {
      var action = actions[i];

      if (i < 8) {
        if (action.new_section) {
          if (action.label) {
            var h4 = document.createElement('h4');
            h4.textContent = action.label;
            toolbar.appendChild(h4);
          }
          currentGroup = document.createElement('div');
          currentGroup.className = 'btn-toolbar';
          toolbar.appendChild(currentGroup);
        } else {
          currentGroup.appendChild(_createActionLink(action, canRunAction));
        }
      } else {
        // Overflow: lazy-create the "More" dropdown on first overflow item
        if (!dropdownGroup) {
          dropdownGroup = true

          var moreBtn = document.createElement('button');
          moreBtn.type = 'button';
          moreBtn.className = 'btn btn-light dropdown-toggle';
          moreBtn.setAttribute('data-bs-toggle', 'dropdown');
          moreBtn.setAttribute('aria-expanded', 'false');
          moreBtn.textContent = 'More';
          currentGroup.appendChild(moreBtn);

          dropdownUl = document.createElement('ul');
          dropdownUl.className = 'dropdown-menu';
          currentGroup.appendChild(dropdownUl);
        }

        if (action.new_section) {
          var dividerLi = document.createElement('li');
          var divider = document.createElement('hr');
          divider.className = 'dropdown-divider';
          dividerLi.appendChild(divider);
          dropdownUl.appendChild(dividerLi);
          if (action.label) {
            var headerLi = document.createElement('li');
            var header = document.createElement('h6');
            header.className = 'dropdown-header';
            header.textContent = action.label;
            headerLi.appendChild(header);
            dropdownUl.appendChild(headerLi);
          }
        } else {
          var li = document.createElement('li');
          li.appendChild(_createDropdownItem(action, canRunAction));
          dropdownUl.appendChild(li);
        }
      }
    }
  }


  // Build a standard action <a> button
  function _createActionLink(action, canRunAction) {
    var a = document.createElement('a');
    var classes = ['btn', 'btn-default', 'justify-content-start'];
    if (!action.not_dialog) classes.push('open-dialog');
    if (_isDisabled(action, canRunAction)) classes.push('disabled');
    a.className = classes.join(' ');
    a.href = action.url;
    a.title = action.label;
    a.setAttribute('data-role', action.role || '');
    a.setAttribute('data-test', action.label);
    _applyAttrs(a, action.attrs);

    if (action.icon_classes) {
      var icon = document.createElement('i');
      icon.className = action.icon_classes;
      a.appendChild(icon);
    }
    var span = document.createElement('span');
    span.className = 'action-label';
    span.textContent = action.label;
    a.appendChild(span);
    return a;
  }


  // Build a dropdown <a> item
  function _createDropdownItem(action, canRunAction) {
    var a = document.createElement('a');
    var classes = ['dropdown-item'];
    if (!action.not_dialog) classes.push('open-dialog');
    if (_isDisabled(action, canRunAction)) classes.push('disabled');
    a.className = classes.join(' ');
    a.href = action.url;
    a.title = action.label;
    a.setAttribute('data-role', action.role || '');
    a.setAttribute('data-test', action.label);
    a.setAttribute('role','button');
    _applyAttrs(a, action.attrs);

    if (action.icon_classes) {
      var icon = document.createElement('i');
      icon.className = action.icon_classes;
      a.appendChild(icon);
    }
    a.appendChild(document.createTextNode(action.label));
    return a;
  }


  // Only allow data-* and aria-* extra attributes to prevent attribute injection
  function _applyAttrs(el, attrs) {
    if (!attrs) return;
    Object.keys(attrs).forEach(function (name) {
      if (/^data-/.test(name) || /^aria-/.test(name)) {
        el.setAttribute(name, attrs[name]);
      }
    });
  }


  function _isDisabled(action, canRunAction) {
    return action.disabled || (!canRunAction && !action.always_allow_to_run);
  }


  // ─── Offline / live-updates UI ────────────────────────────────────────────

  function _showLiveUpdatesInfo() {
    var el = document.getElementById('server-details-live-updates-info');
    if (el) el.classList.remove('d-none');
  }

  function _hideOfflineAlert() {
    var el = document.getElementById('offline-alert');
    if (el) el.classList.add('d-none');
  }

  function _showOfflineAlert() {
    _showLiveUpdatesInfo();
    var el = document.getElementById('offline-alert');
    if (el) el.classList.remove('d-none');
  }

  // Callback for the org-panel c2.include reload
  function _stopUpdatesOnOrgPanelFailure(el, succeeded) {
    if (succeeded) {
      _hideOfflineAlert();
    } else {
      logger.error('org panel data-include failed for el:', el);
      stopUpdates();
      _showOfflineAlert();
    }
  }


  // ─── Timer management ─────────────────────────────────────────────────────

  function startUpdates() {
    logger.debug('startUpdates');
    if (updateTimer) {
      logger.debug('  timers already set, skipping.');
      return;
    }
    logger.debug('Setting up short interval: ' + durations.shortInterval / 1000 + 's');
    updateTimer = setInterval(reloadSections, durations.shortInterval);

    mediumTimeout = setTimeout(function () {
      logger.debug('Switching to medium interval: ' + durations.mediumInterval / 1000 + 's');
      stopUpdates();
      updateTimer = setInterval(reloadSections, durations.mediumInterval);
    }, durations.mediumStart);

    longTimeout = setTimeout(function () {
      logger.debug('Switching to long interval: ' + durations.longInterval / 1000 + 's');
      stopUpdates();
      updateTimer = setInterval(reloadSections, durations.longInterval);
    }, durations.longStart);
  }


  function stopUpdates() {
    logger.debug('stopUpdates');
    clearInterval(updateTimer);
    clearTimeout(mediumTimeout);
    clearTimeout(longTimeout);
    updateTimer = 0;
    mediumTimeout = 0;
    longTimeout = 0;
  }


  // ─── Event handlers ───────────────────────────────────────────────────────

  function _setupPageVisibilityHandler() {
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        logger.debug('page hidden');
        stopUpdates();
      } else {
        logger.debug('page visible');
        startUpdates();
      }
    });
  }


  function _setupRefreshButtonHandler() {
    $(document).on('click', '#refresh-server-details', function () {
      stopUpdates();
      reloadSections();
      startUpdates();
    });
  }


  // ─── Exports ──────────────────────────────────────────────────────────────

  c2[__module__] = {
    liveUpdates: liveUpdates,
    stopUpdates: stopUpdates
  };

})('server', window.c2, window.jQuery);
